import React, { useState, memo, startTransition } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Avatar,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonIcon from "@mui/icons-material/Person";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { useTabContext } from "../contexts/TabContext";
import { useAuth } from "../contexts/AuthContext";

const HELP_DOCS_DRIVE_URL =
  "https://drive.google.com/drive/folders/1KZBhxBDnjZ9AmGEOKSNrXhE3vn5kFgw3?usp=drive_link";

const ContentTabs = memo(({
  userPhotoURL = "",
  userDisplayName = "",
  onProfileClick,
}) => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { tabs, activeTabId, switchTab, closeTab } = useTabContext();
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const userMenuOpen = Boolean(userMenuAnchor);

  if (tabs.length === 0) return null;

  const handleChange = (_, newTabId) => {
    const tab = tabs.find((t) => t.id === newTabId);
    startTransition(() => {
      switchTab(newTabId);
      if (tab) navigate(tab.path);
    });
  };

  const handleCloseClick = (e, tabId) => {
    e.stopPropagation();
    closeTab(tabId);
  };

  const handleOpenUserMenu = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setUserMenuAnchor(null);
  };

  const handleAccountClick = () => {
    handleCloseUserMenu();
    if (onProfileClick) {
      onProfileClick();
    }
  };

  const handleLogoutClick = async () => {
    handleCloseUserMenu();
    await logout();
    navigate("/login");
  };

  const handleUserMenuKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleOpenUserMenu(event);
    }
  };

  return (
    <Box
      sx={{
        flexShrink: 0,
        display: "flex",
        alignItems: "stretch",
        minHeight: 40,
        borderBottom: 1,
        borderColor: "divider",
        backgroundColor: "#f5f5f5",
        zIndex: 1200,
      }}
    >
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        <Tabs
          value={activeTabId}
          onChange={handleChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          TabIndicatorProps={{ sx: { transition: "none" } }}
          sx={{
            minHeight: 40,
            "& .MuiTabs-flexContainer": {
              gap: 0,
            },
            "& .MuiTabs-indicator": {
              backgroundColor: "#1976d2",
            },
            "& .MuiTabs-scroller": {
              overflowX: "auto",
            },
          }}
        >
          {tabs.map((tab) => {
            const isActive = tab.id === activeTabId;
            return (
              <Tab
                key={tab.id}
                value={tab.id}
                label={
                  <Box
                    component="span"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      minWidth: 0,
                    }}
                  >
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: 120,
                      }}
                    >
                      {tab.label}
                    </span>
                    {tabs.length > 1 && (
                      <Tooltip title="关闭标签" arrow>
                        <IconButton
                          size="small"
                          onClick={(e) => handleCloseClick(e, tab.id)}
                          aria-label="关闭标签"
                          sx={{
                            p: 0.25,
                            ml: 0.25,
                            "&:hover": {
                              backgroundColor: "rgba(0,0,0,0.08)",
                              "& .MuiSvgIcon-root": {
                                color: "error.main",
                              },
                            },
                          }}
                        >
                          <CloseIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                }
                sx={{
                  minHeight: 40,
                  py: 0,
                  px: 1.5,
                  textTransform: "none",
                  fontSize: "0.875rem",
                  minWidth: "auto",
                  backgroundColor: isActive ? "#fff" : "transparent",
                  borderRight: "1px solid #e0e0e0",
                  borderRadius: 0,
                  transition: "none",
                  "&.Mui-selected": {
                    backgroundColor: "#fff",
                    color: "#1976d2",
                    fontWeight: 500,
                  },
                  "&:hover": {
                    backgroundColor: isActive ? "#fff" : "rgba(0,0,0,0.04)",
                  },
                }}
              />
            );
          })}
        </Tabs>
      </Box>

      <Box
        id="user-menu-trigger"
        role="button"
        tabIndex={0}
        aria-haspopup="menu"
        aria-expanded={userMenuOpen}
        aria-label="打开用户菜单"
        onClick={handleOpenUserMenu}
        onKeyDown={handleUserMenuKeyDown}
        sx={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          py: 0.5,
          borderLeft: "1px solid #e0e0e0",
          backgroundColor: "#fff",
          maxWidth: { xs: 140, sm: 220 },
          cursor: "pointer",
          "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
          "&:focus-visible": {
            outline: "2px solid",
            outlineColor: "primary.main",
            outlineOffset: 2,
          },
        }}
      >
        {userPhotoURL ? (
          <Avatar
            src={userPhotoURL}
            alt=""
            sx={{
              width: 32,
              height: 32,
              border: "2px solid #1976d2",
              flexShrink: 0,
              pointerEvents: "none",
            }}
          />
        ) : (
          <Avatar
            src="/favicon.svg"
            alt=""
            sx={{
              width: 32,
              height: 32,
              border: "2px solid #1976d2",
              flexShrink: 0,
              pointerEvents: "none",
            }}
          />
        )}
        {userDisplayName && (
          <Typography
            variant="body2"
            noWrap
            sx={{
              color: "text.secondary",
              fontSize: "0.8125rem",
              display: { xs: "none", sm: "block" },
              minWidth: 0,
              pointerEvents: "none",
            }}
          >
            {userDisplayName}
          </Typography>
        )}
      </Box>
      <Menu
        anchorEl={userMenuAnchor}
        open={userMenuOpen}
        onClose={handleCloseUserMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        MenuListProps={{ "aria-labelledby": "user-menu-trigger" }}
      >
        <MenuItem onClick={handleAccountClick}>
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="我的账户" />
        </MenuItem>
        <MenuItem
          component="a"
          href={HELP_DOCS_DRIVE_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleCloseUserMenu}
          sx={{ textDecoration: "none", color: "inherit" }}
        >
          <ListItemIcon>
            <DescriptionOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="帮助文档" />
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogoutClick}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="退出登录" />
        </MenuItem>
      </Menu>
    </Box>
  );
});

ContentTabs.displayName = "ContentTabs";

export default ContentTabs;
